import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type { PaginatedResponse } from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import { useAdminToast } from "../../context/AdminToastContext";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import { useAlertModal } from "../../components/admin/AlertModal";
import CategoryPromotionToggle from "../../components/admin/CategoryPromotionToggle";
import DateRangeFilter, { type DateFilterEntry } from "../../components/admin/DateRangeFilter";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

const NEWS_DATE_FIELDS = [
  { value: "created_at", label: "Created" },
  { value: "updated_at", label: "Updated" },
  { value: "published_at", label: "Published" },
];

const CATEGORIES = ["general", "airline", "hotel", "destination", "visa", "regulatory", "industry", "technology", "cruise", "deals"];
const STATUSES = ["all", "published", "draft", "archived"];

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALLOWED_ATTACHMENT_EXTS = ["pdf", "doc", "docx"];

interface StagedFile {
  file: File;
  preview?: string;
}

interface ExistingGalleryItem {
  key: string;
  url: string;
}

interface ExistingAttachmentItem {
  key: string;
  url: string;
  name: string;
  type: string;
  thumbnail_url?: string | null;
}

const EMPTY_FORM = {
  title: "",
  summary: "",
  body: "",
  category: "general",
  tags: "",
  destinations: "",
  source: "",
  source_url: "",
  source_author: "",
  status: "published",
};

const AdminNewsPage: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilters, setDateFilters] = useState<DateFilterEntry[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { alertModal, showAlert } = useAlertModal();
  const { showError, showSuccess } = useAdminToast();
  const [backfillingImages, setBackfillingImages] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  useEffect(() => { if (formError) showError(formError); }, [formError, showError]);
  const [loadingArticle, setLoadingArticle] = useState(false);

  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [existingCoverUrl, setExistingCoverUrl] = useState<string | null>(null);
  const [externalImageUrl, setExternalImageUrl] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState<StagedFile[]>([]);
  const [attachFiles, setAttachFiles] = useState<StagedFile[]>([]);
  const [existingGallery, setExistingGallery] = useState<ExistingGalleryItem[]>([]);
  const [removedGalleryKeys, setRemovedGalleryKeys] = useState<string[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachmentItem[]>([]);
  const [removedAttachmentKeys, setRemovedAttachmentKeys] = useState<string[]>([]);

  const [viewTarget, setViewTarget] = useState<any | null>(null);
  const [loadingView, setLoadingView] = useState(false);

  const [mediaPreview, setMediaPreview] = useState<
    | { kind: "image"; url: string; name?: string }
    | { kind: "doc"; url: string; name: string; type: string; thumbnail_url?: string | null }
    | null
  >(null);

  function pickCoverFile(file: File) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) { setFormError("Cover image must be JPEG, PNG, WebP, or GIF."); return; }
    if (file.size > 10 * 1024 * 1024) { setFormError("Cover image must be under 10 MB."); return; }
    setFormError("");
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setExternalImageUrl("");
    setExistingCoverUrl(null);
  }

  function onCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) pickCoverFile(file);
    e.target.value = "";
  }

  function onCoverDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) pickCoverFile(file);
  }

  function clearCover() {
    setCoverFile(null);
    setCoverPreview(null);
    setExternalImageUrl("");
    setExistingCoverUrl(null);
  }

  function onGalleryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const valid = files.filter((f) => ALLOWED_IMAGE_TYPES.includes(f.type) && f.size <= 10 * 1024 * 1024);
    if (valid.length < files.length) setFormError("Some images skipped (wrong type or > 10 MB).");
    setGalleryFiles((prev) => [...prev, ...valid.map((f) => ({ file: f, preview: URL.createObjectURL(f) }))]);
    e.target.value = "";
  }

  function onAttachChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const valid = files.filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
      return ALLOWED_ATTACHMENT_EXTS.includes(ext) && f.size <= 50 * 1024 * 1024;
    });
    if (valid.length < files.length) setFormError("Some files skipped (only PDF, DOC, DOCX up to 50 MB).");
    setAttachFiles((prev) => [...prev, ...valid.map((f) => ({ file: f }))]);
    e.target.value = "";
  }

  function removeExistingGalleryItem(key: string) {
    setExistingGallery((prev) => prev.filter((g) => g.key !== key));
    setRemovedGalleryKeys((prev) => [...prev, key]);
  }

  function removeExistingAttachment(key: string) {
    setExistingAttachments((prev) => prev.filter((a) => a.key !== key));
    setRemovedAttachmentKeys((prev) => [...prev, key]);
  }

  function resetForm() {
    setForm({ ...EMPTY_FORM });
    clearCover();
    setGalleryFiles([]);
    setAttachFiles([]);
    setExistingGallery([]);
    setRemovedGalleryKeys([]);
    setExistingAttachments([]);
    setRemovedAttachmentKeys([]);
    setFormError("");
    setEditingId(null);
  }

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter !== "all") params.news_status = statusFilter;
      const activeDates = dateFilters.filter((d) => d.from || d.to);
      if (activeDates.length) params.date_filters = JSON.stringify(activeDates);
      setData(await adminService.getNews(params));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, dateFilters]);

  useEffect(() => { fetchNews(); }, [fetchNews]);
  useEffect(() => { setPage(1); }, [pageSize]);

  const handleBackfillCoverImages = async (force = false) => {
    if (backfillingImages) return;
    setBackfillingImages(true);
    try {
      const { checked, upgraded, repaired, cleared, unchanged, failed } =
        await adminService.backfillNewsCoverImages(500, force);
      if (checked === 0) {
        // Without this the button used to look broken: it reported 0/0/0 while
        // blurry articles were still on screen, because every one of them was
        // filtered out of the candidate query.
        showSuccess("No articles needed checking — every cover is already self-hosted and verified.");
      } else {
        showSuccess(
          `Checked ${checked} article(s) — upgraded ${upgraded}, repaired ${repaired}, ` +
            `cleared ${cleared} blurry cover${cleared === 1 ? "" : "s"}, ` +
            `${unchanged} already fine${failed ? `, ${failed} failed` : ""}.`
        );
      }
      fetchNews();
    } catch (err) {
      console.error(err);
      showError("Backfill failed — check the console.");
    } finally {
      setBackfillingImages(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteNews(deleteTarget.id);
      setDeleteTarget(null);
      fetchNews();
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to delete article.");
    } finally {
      setDeleting(false);
    }
  };

  const openView = async (article: any) => {
    setViewTarget(article);
    setLoadingView(true);
    try {
      const full = await adminService.getNewsArticle(article.id);
      setViewTarget(full);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingView(false);
    }
  };

  const openEdit = async (article: any) => {
    resetForm();
    setEditingId(article.id);
    setShowForm(true);
    setFormError("");
    setLoadingArticle(true);
    try {
      const full = await adminService.getNewsArticle(article.id);
      setForm({
        title: full.title || "",
        summary: full.summary || "",
        body: full.body || "",
        category: full.category || "general",
        tags: (full.tags || []).join(", "),
        destinations: (full.destinations || []).join(", "),
        source: full.source || "",
        source_url: full.source_url || "",
        source_author: full.source_author || "",
        status: full.status || "published",
      });
      setExistingCoverUrl(full.cover_image_url || null);
      setExistingGallery(full.gallery || []);
      setExistingAttachments(full.attachments || []);
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || "Failed to load article");
    } finally {
      setLoadingArticle(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setFormError("Title is required"); return; }
    setSubmitting(true);
    setFormError("");
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      if (form.summary.trim()) fd.append("summary", form.summary.trim());
      if (form.body.trim()) fd.append("body", form.body.trim());
      fd.append("category", form.category);
      fd.append("status", form.status);
      if (form.tags.trim()) fd.append("tags", form.tags.trim());
      if (form.destinations.trim()) fd.append("destinations", form.destinations.trim());
      if (form.source.trim()) fd.append("source", form.source.trim());
      if (form.source_url.trim()) fd.append("source_url", form.source_url.trim());
      if (form.source_author.trim()) fd.append("source_author", form.source_author.trim());
      if (!coverFile && externalImageUrl.trim()) fd.append("cover_image_url", externalImageUrl.trim());
      if (coverFile) fd.append("cover_image", coverFile, coverFile.name);
      for (const { file } of galleryFiles) fd.append("gallery", file, file.name);
      for (const { file } of attachFiles) fd.append("attachments", file, file.name);

      if (editingId) {
        if (removedGalleryKeys.length) fd.append("remove_gallery_keys", removedGalleryKeys.join(","));
        if (removedAttachmentKeys.length) fd.append("remove_attachment_keys", removedAttachmentKeys.join(","));
        await adminService.updateNewsWithFiles(editingId, fd);
      } else {
        await adminService.createNewsWithFiles(fd);
      }
      setShowForm(false);
      resetForm();
      fetchNews();
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || `Failed to ${editingId ? "update" : "create"} article`);
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (s: string) => {
    const map: Record<string, string> = {
      published: "bg-green-500/10 text-green-400",
      draft: "bg-amber-500/10 text-amber-400",
      archived: "bg-gray-500/10 text-gray-400",
    };
    return map[s] || "bg-gray-500/10 text-gray-400";
  };

  return (
    <>
      <div>
        <AdminPageHeader
          title="News"
          subtitle="Manage industry news from sellers & admin"
          meta={`${data?.total ?? 0} articles`}
          actions={
            <>
              <RefreshButton onRefresh={fetchNews} />
              <button
                onClick={() => handleBackfillCoverImages(false)}
                disabled={backfillingImages}
                title="Re-checks ingested RSS articles' cover images and clears/upgrades any that would look blurry when upscaled into a card/hero box"
                className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {backfillingImages ? "Fixing…" : "Fix Cover Images"}
              </button>
              <button
                onClick={() => handleBackfillCoverImages(true)}
                disabled={backfillingImages}
                title="Re-downloads and re-measures EVERY ingested RSS article's cover, including ones that already look fine. Slow, but never a no-op."
                className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                Re-verify All
              </button>
              <button
                onClick={() => { resetForm(); setShowForm(true); setFormError(""); }}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add News
              </button>
            </>
          }
        />

        <CategoryPromotionToggle slug="news" name="News" />

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <input
            type="text"
            placeholder="Search news..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-72"
          />
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === s ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <DateRangeFilter
            fields={NEWS_DATE_FIELDS}
            value={dateFilters}
            onChange={(v) => { setDateFilters(v); setPage(1); }}
          />
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  {/* Cover thumbnail: the whole point of "Fix Cover Images" is the
                      picture, so it has to be visible from the list — otherwise a
                      blurry or missing cover is only discoverable by opening every
                      article one at a time. */}
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Cover</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Title</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Category</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Author</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Source</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Views</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Created</th>
                  <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
                ) : data?.items.length === 0 ? (
                  <tr><td colSpan={9} className="py-20 text-center text-gray-500">No articles found</td></tr>
                ) : (
                  data?.items.map((article) => (
                    <tr key={article.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4">
                        {article.cover_image_url ? (
                          <img
                            src={article.cover_image_url}
                            alt=""
                            loading="lazy"
                            onClick={() => setMediaPreview({ kind: "image", url: article.cover_image_url! })}
                            className="w-20 h-12 object-cover rounded-md border border-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                          />
                        ) : (
                          <div
                            title="No cover image — the frontend renders a placeholder for this article"
                            className="w-20 h-12 rounded-md border border-dashed border-gray-700 flex items-center justify-center text-[10px] text-gray-600"
                          >
                            none
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-white font-medium truncate max-w-xs">{article.title || "Untitled"}</p>
                        {article.summary && <p className="text-gray-500 text-xs truncate max-w-xs mt-0.5">{article.summary}</p>}
                      </td>
                      <td className="py-3 px-4 text-gray-400 capitalize">{article.category || "—"}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor(article.status)}`}>
                          {article.status || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400">{article.author_name || "—"}</td>
                      <td className="py-3 px-4 text-gray-400 truncate max-w-[120px]">{article.source || "—"}</td>
                      <td className="py-3 px-4 text-gray-400">{article.view_count ?? 0}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{article.created_at ? formatIST(article.created_at) : "—"}</td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button onClick={() => openView(article)} className="px-2 py-1 text-xs text-gray-300 hover:bg-gray-700/50 rounded-md transition-colors">View</button>
                        <button onClick={() => openEdit(article)} className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors">Edit</button>
                        <button onClick={() => setDeleteTarget(article)} className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {data && data.total > 0 && (
          <Pagination
            page={page - 1}
            pageSize={pageSize}
            total={data.total}
            onPageChange={(p) => setPage(p + 1)}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>

      {/* Add / Edit News Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white">{editingId ? "Edit News Article" : "Add News Article"}</h2>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {loadingArticle ? (
              <div className="p-10 text-center text-gray-500">Loading article...</div>
            ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{formError}</p>}

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Title <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Article title..."
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Cover Image</label>
                {coverPreview || existingCoverUrl ? (
                  <div className="relative rounded-xl overflow-hidden border border-gray-700">
                    <button
                      type="button"
                      onClick={() => setMediaPreview({ kind: "image", url: coverPreview || existingCoverUrl || "" })}
                      className="block w-full"
                    >
                      <img src={coverPreview || existingCoverUrl || ""} alt="Cover preview" className="w-full h-44 object-cover" />
                    </button>
                    <button type="button" onClick={clearCover} className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 hover:bg-black text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <label
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onCoverDrop}
                    className={`flex flex-col items-center justify-center h-32 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                      dragOver ? "border-indigo-400 bg-indigo-500/10" : "border-gray-700 bg-gray-800/50 hover:border-indigo-500/50"
                    }`}
                  >
                    <span className="text-2xl mb-1">🖼️</span>
                    <p className="text-sm text-gray-400">Drag & drop or click to browse</p>
                    <p className="text-xs text-gray-600 mt-0.5">JPEG, PNG, WebP, GIF · max 10 MB</p>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onCoverChange} />
                  </label>
                )}
                {!coverFile && !existingCoverUrl && (
                  <input
                    type="text"
                    placeholder="…or paste an image URL"
                    value={externalImageUrl}
                    onChange={(e) => setExternalImageUrl(e.target.value)}
                    className="mt-2 w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Gallery Images <span className="text-gray-600">(optional · multiple)</span></label>
                <div className="flex flex-wrap gap-2">
                  {existingGallery.map((g) => (
                    <div key={g.key} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-700 group">
                      <button type="button" onClick={() => setMediaPreview({ kind: "image", url: g.url })} className="block w-full h-full">
                        <img src={g.url} alt="" className="w-full h-full object-cover" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeExistingGalleryItem(g.key)}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {galleryFiles.map((sf, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-700 group">
                      <button type="button" onClick={() => setMediaPreview({ kind: "image", url: sf.preview || "" })} className="block w-full h-full">
                        <img src={sf.preview} alt={sf.file.name} className="w-full h-full object-cover" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setGalleryFiles((p) => p.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-700 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500/50 transition-colors">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={onGalleryChange} />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Attachments <span className="text-gray-600">(optional · PDF, DOC, DOCX)</span></label>
                <div className="space-y-2">
                  {existingAttachments.map((a) => (
                    <div key={a.key} className="flex items-center gap-3 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2">
                      {a.thumbnail_url ? (
                        <img src={a.thumbnail_url} alt="" className="w-8 h-8 object-cover rounded" />
                      ) : (
                        <span className="text-lg">{a.name.endsWith(".pdf") ? "📄" : "📝"}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setMediaPreview({ kind: "doc", url: a.url, name: a.name, type: a.type, thumbnail_url: a.thumbnail_url })}
                        className="text-sm text-gray-300 flex-1 truncate text-left hover:text-indigo-400 hover:underline"
                      >
                        {a.name}
                      </button>
                      <button type="button" onClick={() => removeExistingAttachment(a.key)} className="text-gray-500 hover:text-red-400 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {attachFiles.map((sf, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2">
                      <span className="text-lg">{sf.file.name.endsWith(".pdf") ? "📄" : "📝"}</span>
                      <span className="text-sm text-gray-300 flex-1 truncate">{sf.file.name}</span>
                      <span className="text-xs text-gray-500">{(sf.file.size / 1024 / 1024).toFixed(1)} MB</span>
                      <button type="button" onClick={() => setAttachFiles((p) => p.filter((_, i) => i !== idx))} className="text-gray-500 hover:text-red-400 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-700 rounded-xl cursor-pointer hover:border-indigo-500/50 transition-colors w-fit">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span className="text-sm text-gray-400">Attach PDF / DOCX</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      multiple
                      className="hidden"
                      onChange={onAttachChange}
                    />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Summary</label>
                <textarea
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  rows={2}
                  placeholder="Short summary shown in list view..."
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Body</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={6}
                  placeholder="Full article content (supports markdown)..."
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Source</label>
                  <input
                    type="text"
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    placeholder="e.g. Travel Weekly"
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Source URL</label>
                  <input
                    type="url"
                    value={form.source_url}
                    onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Author</label>
                <input
                  type="text"
                  value={form.source_author}
                  onChange={(e) => setForm({ ...form, source_author: e.target.value })}
                  placeholder="e.g. Rahul Sharma"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Tags <span className="text-gray-600">(comma-separated)</span></label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="e.g. visa, india, travel"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Destinations <span className="text-gray-600">(comma-separated)</span></label>
                <input
                  type="text"
                  value={form.destinations}
                  onChange={(e) => setForm({ ...form, destinations: e.target.value })}
                  placeholder="e.g. Maldives, Dubai, Goa"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  {submitting ? (editingId ? "Saving..." : "Publishing...") : (editingId ? "Save Changes" : "Publish Article")}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white truncate pr-4">{viewTarget.title || "Untitled"}</h2>
              <button onClick={() => setViewTarget(null)} className="text-gray-400 hover:text-white transition-colors shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              {loadingView && <p className="text-gray-500 text-sm">Loading full article...</p>}

              {viewTarget.cover_image_url && (
                <button
                  type="button"
                  onClick={() => setMediaPreview({ kind: "image", url: viewTarget.cover_image_url })}
                  className="block w-full"
                >
                  <img src={viewTarget.cover_image_url} alt="Cover" className="w-full h-56 object-cover rounded-xl border border-gray-700 hover:opacity-90 transition-opacity" />
                </button>
              )}

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`px-2 py-1 rounded-md font-medium ${statusColor(viewTarget.status)}`}>{viewTarget.status || "—"}</span>
                {viewTarget.category && <span className="px-2 py-1 rounded-md bg-gray-800 text-gray-400 capitalize">{viewTarget.category}</span>}
                {viewTarget.source && <span className="px-2 py-1 rounded-md bg-gray-800 text-gray-400">{viewTarget.source}</span>}
                {viewTarget.source_author && <span className="px-2 py-1 rounded-md bg-gray-800 text-gray-400">by {viewTarget.source_author}</span>}
                {viewTarget.created_at && <span className="px-2 py-1 rounded-md bg-gray-800 text-gray-500">{formatIST(viewTarget.created_at)}</span>}
              </div>

              {viewTarget.source_url && (
                <a href={viewTarget.source_url} target="_blank" rel="noreferrer" className="text-sm text-indigo-400 hover:underline break-all">{viewTarget.source_url}</a>
              )}

              {viewTarget.summary && (
                <p className="text-gray-300 text-sm italic border-l-2 border-gray-700 pl-3">{viewTarget.summary}</p>
              )}

              {viewTarget.body && (
                <div className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">{viewTarget.body}</div>
              )}

              {(viewTarget.tags?.length > 0 || viewTarget.destinations?.length > 0) && (
                <div className="flex flex-wrap gap-2">
                  {(viewTarget.tags || []).map((t: string) => (
                    <span key={t} className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400 text-xs">#{t}</span>
                  ))}
                  {(viewTarget.destinations || []).map((d: string) => (
                    <span key={d} className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs">📍 {d}</span>
                  ))}
                </div>
              )}

              {viewTarget.gallery?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Gallery</p>
                  <div className="grid grid-cols-4 gap-2">
                    {viewTarget.gallery.map((g: { key: string; url: string }) => (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => setMediaPreview({ kind: "image", url: g.url })}
                        className="block aspect-square rounded-lg overflow-hidden border border-gray-700"
                      >
                        <img src={g.url} alt="" className="w-full h-full object-cover hover:opacity-80 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {viewTarget.attachments?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Attachments</p>
                  <div className="space-y-2">
                    {viewTarget.attachments.map((a: ExistingAttachmentItem) => (
                      <button
                        key={a.key || a.url}
                        type="button"
                        onClick={() => setMediaPreview({ kind: "doc", url: a.url, name: a.name, type: a.type, thumbnail_url: a.thumbnail_url })}
                        className="w-full flex items-center gap-3 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 hover:border-indigo-500/50 transition-colors text-left"
                      >
                        {a.thumbnail_url ? (
                          <img src={a.thumbnail_url} alt="" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <span className="text-lg">{a.name?.endsWith(".pdf") ? "📄" : "📝"}</span>
                        )}
                        <span className="text-sm text-gray-300 flex-1 truncate">{a.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { const t = viewTarget; setViewTarget(null); openEdit(t); }}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  Edit Article
                </button>
                <button
                  onClick={() => setViewTarget(null)}
                  className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Preview Lightbox (images + PDF/DOCX attachments, in-page) */}
      {mediaPreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setMediaPreview(null)}
        >
          <div
            className={`bg-gray-900 border border-gray-800 rounded-2xl w-full h-[85vh] ${mediaPreview.kind === "doc" && mediaPreview.type === "application/pdf" ? "max-w-4xl" : "max-w-3xl"} overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-800 shrink-0">
              <p className="text-sm font-medium text-white truncate pr-4">{mediaPreview.kind === "doc" ? mediaPreview.name : "Image preview"}</p>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={mediaPreview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-gray-400 hover:text-indigo-400 px-2 py-1 rounded-md hover:bg-gray-800 transition-colors"
                >
                  Open in new tab
                </a>
                <button onClick={() => setMediaPreview(null)} className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center bg-black/30">
              {mediaPreview.kind === "image" ? (
                <img src={mediaPreview.url} alt={mediaPreview.name || "Preview"} className="w-full h-full object-contain" />
              ) : mediaPreview.type === "application/pdf" ? (
                <iframe src={mediaPreview.url} title={mediaPreview.name} className="w-full h-full border-0" />
              ) : (
                <div className="p-8 w-full h-full flex flex-col items-center gap-4 text-center">
                  {mediaPreview.thumbnail_url ? (
                    <img src={mediaPreview.thumbnail_url} alt={mediaPreview.name} className="flex-1 w-full rounded-lg border border-gray-700 object-contain min-h-0" />
                  ) : (
                    <span className="text-6xl">📝</span>
                  )}
                  <p className="text-gray-400 text-sm shrink-0">Word documents can't be rendered inline — showing the first-page preview.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete News Article"
          description={`"${deleteTarget.title || "This article"}" will be permanently removed.`}
          items={["Delete article from database", "Remove from all feeds"]}
          confirmLabel="Delete Article"
          busy={deleting}
          busyLabel="Deleting…"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {alertModal}
    </>
  );
};

export default AdminNewsPage;
